import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Link } from 'react-router-dom';
import { Filter, Info, Calendar, Eye, ListChecks, TrendingUp } from 'lucide-react';
import SearchableSelect from '@/client/components/admin/SearchableSelect';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { Skeleton } from '@/components/ui/skeleton';
import { type EntityStatus } from '@/client/api/listing-management/listing-stats.query';
import { useAdminAnalyticsSummaryQuery } from '@/client/api/analytics/analytics.query';
import type { AnalyticsSummaryBucket } from '@/client/types/analytics';

const numberFmt = new Intl.NumberFormat('en-US');

/**
 * Only the two windows `/admin/listings/analytics/summary` actually computes
 * (`created_last_7d` / `created_last_30d`). The previous dropdown offered five
 * ranges that the endpoint has no parameter for, so four of them did nothing.
 */
const periodOptions = [
  { value: '7', label: 'Last 7 Days' },
  { value: '30', label: 'Last 30 Days' },
];

/**
 * Status → status-palette role. LIVE is the only "good" state; REVIEW is
 * pending attention; REJECTED is critical and SUSPENDED serious. Everything
 * else is a neutral resting state and reads gray on purpose.
 *
 * Contrast on the light surface is below 3:1 for warning/serious, so these
 * bars are always shipped with an axis label AND a direct value label — the
 * color never carries the meaning by itself.
 */
const STATUS_COLOR: Record<string, string> = {
  LIVE: '#0ca30c',
  REVIEW: '#fab219',
  SUSPENDED: '#ec835a',
  REJECTED: '#d03b3b',
  DRAFT: '#9ca3af',
  INACTIVE: '#9ca3af',
  ARCHIVED: '#9ca3af',
  DELETED: '#9ca3af',
  UNKNOWN: '#9ca3af',
};

/** Display order: live first, then the states an admin acts on, then resting. */
const STATUS_ORDER: EntityStatus[] = [
  'LIVE',
  'REVIEW',
  'REJECTED',
  'SUSPENDED',
  'DRAFT',
  'INACTIVE',
  'ARCHIVED',
  'DELETED',
];

const CATEGORY_BLUE = '#2a78d6';

const ChartCard: React.FC<{
  title: string;
  note?: string;
  isLoading?: boolean;
  isEmpty?: boolean;
  children: React.ReactNode;
}> = ({ title, note, isLoading, isEmpty, children }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-6">
    <h3 className="text-base font-semibold text-gray-900">{title}</h3>
    {note && <p className="mt-0.5 text-xs text-gray-400">{note}</p>}
    <div className="mt-4 h-80">
      {isLoading ? (
        <Skeleton className="h-full w-full rounded" />
      ) : isEmpty ? (
        <div className="flex h-full items-center justify-center text-sm text-gray-400">
          No data returned for this scope
        </div>
      ) : (
        children
      )}
    </div>
  </div>
);

const MetricCard: React.FC<{
  title: string;
  value: number;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Set when the backend has no source for this metric — shows 0, says why. */
  unavailable?: boolean;
  isLoading?: boolean;
}> = ({ title, value, hint, icon: Icon, unavailable, isLoading }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-5">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm text-gray-500">{title}</p>
        {isLoading ? (
          <Skeleton className="mt-2 h-8 w-20" />
        ) : (
          <p
            className={`mt-1.5 text-3xl font-semibold tabular-nums ${
              unavailable ? 'text-gray-300' : 'text-gray-900'
            }`}
          >
            {numberFmt.format(value)}
          </p>
        )}
      </div>
      <div
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${
          unavailable ? 'bg-gray-50 text-gray-300' : 'bg-blue-50 text-blue-600'
        }`}
      >
        <Icon className="h-5 w-5" />
      </div>
    </div>
    {unavailable ? (
      <p className="mt-3 inline-flex items-center gap-1 text-xs text-gray-400">
        <Info className="h-3 w-3" />
        Not tracked by the API
      </p>
    ) : (
      hint && <p className="mt-3 text-xs text-gray-500">{hint}</p>
    )}
  </div>
);

/** Horizontal bars with direct value labels, largest first. */
const RankedBars: React.FC<{ data: { name: string; count: number }[] }> = ({ data }) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data} layout="vertical" margin={{ top: 4, right: 48, bottom: 4, left: 8 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#f0efec" horizontal={false} />
      <XAxis
        type="number"
        tick={{ fontSize: 12, fill: '#52514e' }}
        tickLine={false}
        axisLine={false}
        tickFormatter={(v) => numberFmt.format(v)}
      />
      <YAxis
        type="category"
        dataKey="name"
        tick={{ fontSize: 12, fill: '#52514e' }}
        tickLine={false}
        axisLine={false}
        width={140}
      />
      <Tooltip cursor={{ fill: '#f7f7f5' }} formatter={(v: number) => [numberFmt.format(v), 'Listings']} />
      <Bar dataKey="count" fill={CATEGORY_BLUE} radius={[0, 4, 4, 0]} maxBarSize={22}>
        <LabelList
          dataKey="count"
          position="right"
          fontSize={11}
          fill="#52514e"
          formatter={(v: number) => numberFmt.format(v)}
        />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const bucketRows = (buckets: AnalyticsSummaryBucket[] | undefined, noneLabel: string) =>
  (buckets ?? []).map((b) => ({ name: b.name || noneLabel, count: b.count }));

/** Name / count / share-of-total table for the top-N lists. */
const RankedTable: React.FC<{
  title: string;
  note: string;
  column: string;
  rows: { name: string; count: number }[];
  total: number;
  isLoading?: boolean;
}> = ({ title, note, column, rows, total, isLoading }) => (
  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
    <div className="border-b border-gray-200 px-6 py-4">
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      <p className="mt-0.5 text-xs text-gray-400">{note}</p>
    </div>
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          {[column, 'Listings', 'Share'].map((h) => (
            <th
              key={h}
              className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 bg-white">
        {isLoading
          ? Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}>
                <td colSpan={3} className="px-6 py-3">
                  <Skeleton className="h-4 w-full" />
                </td>
              </tr>
            ))
          : rows.length === 0
            ? (
              <tr>
                <td colSpan={3} className="px-6 py-8 text-center text-sm text-gray-400">
                  No data returned for this scope
                </td>
              </tr>
            )
            : rows.map((r) => (
                <tr key={r.name} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm text-gray-900">{r.name}</td>
                  <td className="px-6 py-3 text-sm tabular-nums text-gray-600">{numberFmt.format(r.count)}</td>
                  <td className="px-6 py-3 text-sm tabular-nums text-gray-500">
                    {total ? `${((r.count / total) * 100).toFixed(1)}%` : '-'}
                  </td>
                </tr>
              ))}
      </tbody>
    </table>
  </div>
);

const ListingAnalytics = () => {
  useBreadcrumbs([
    { label: 'Listing Management', href: '#' },
    { label: 'Analytics and Reports' },
  ]);

  const [period, setPeriod] = useState('30');
  const [category, setCategory] = useState('all');

  const scopeEtid = category === 'all' ? undefined : Number(category);

  // Single source of truth: the admin analytics summary. Its `top_categories`
  // already carry a per-category `by_status`, so category scoping and the
  // "active per category" column derive from it — no per-category fan-out.
  const { data: summaryData, isLoading, error } = useAdminAnalyticsSummaryQuery();

  const stats = summaryData;
  const overallLoading = isLoading;
  const perCategoryLoading = isLoading;

  const topCategories = useMemo(
    () => summaryData?.top_categories ?? [],
    [summaryData],
  );

  // When a category is selected, scope the status chart to that category's own
  // by_status; otherwise use the global breakdown.
  const scopedByStatus = useMemo<Record<string, number>>(() => {
    if (!scopeEtid) return summaryData?.by_status ?? {};
    return topCategories.find((c) => c.etid === scopeEtid)?.by_status ?? {};
  }, [scopeEtid, summaryData, topCategories]);

  // etid → LIVE count, straight from the summary's per-category by_status.
  const activeByEtid = useMemo(
    () =>
      topCategories.reduce<Record<number, number | undefined>>((acc, c) => {
        acc[c.etid] = c.by_status?.LIVE ?? 0;
        return acc;
      }, {}),
    [topCategories],
  );

  const categoryOptions = useMemo(
    () => [
      { value: 'all', label: 'All Categories' },
      ...topCategories.map((c) => ({
        value: String(c.etid),
        label: c.name || `Category ${c.etid}`,
      })),
    ],
    [topCategories],
  );

  const statusData = useMemo(() => {
    const by = scopedByStatus;
    return STATUS_ORDER.filter((s) => by[s] !== undefined).map((s) => ({
      status: s.charAt(0) + s.slice(1).toLowerCase(),
      count: by[s] ?? 0,
      fill: STATUS_COLOR[s],
    }));
  }, [scopedByStatus]);

  const categoryChartData = useMemo(
    () =>
      topCategories.slice(0, 10).map((c) => ({
        name: c.name || `#${c.etid}`,
        count: c.count,
      })),
    [topCategories],
  );

  const newInPeriod =
    (period === '7' ? stats?.created_last_7d : stats?.created_last_30d) ?? 0;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-8xl rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          Couldn’t load listing analytics — {(error as Error).message}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-8xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Listings Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Live aggregates over the Entity table
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-4">
          <SearchableSelect
            options={periodOptions}
            value={period}
            onChange={setPeriod}
            placeholder="Select Period"
            icon={Calendar}
            className="w-48"
          />
          <SearchableSelect
            options={categoryOptions}
            value={category}
            onChange={setCategory}
            placeholder="Select Category"
            icon={Filter}
            className="w-56"
          />
        </div>

        {/* Metrics */}
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Active Listings"
            value={stats?.by_status?.LIVE ?? 0}
            hint={`${numberFmt.format(stats?.total ?? 0)} total incl. archived & deleted`}
            icon={ListChecks}
            isLoading={isLoading}
          />
          <MetricCard
            title={`New Listings (${period}d)`}
            value={newInPeriod}
            hint={
              period === '30'
                ? `${numberFmt.format(stats?.created_last_7d ?? 0)} in the last 7 days`
                : `${numberFmt.format(stats?.created_last_30d ?? 0)} in the last 30 days`
            }
            icon={TrendingUp}
            isLoading={isLoading}
          />
          <MetricCard
            title="Average Time to Sell"
            value={0}
            icon={Calendar}
            unavailable
          />
          <MetricCard
            title="Total Views"
            value={stats?.total_views ?? 0}
            hint="All-time listing page views"
            icon={Eye}
            isLoading={isLoading}
          />
        </div>

        {/* Charts */}
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ChartCard
            title="Listings by Status"
            note={
              scopeEtid
                ? 'Scoped to the selected category'
                : 'All categories'
            }
            isLoading={isLoading}
            isEmpty={statusData.length === 0}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData} margin={{ top: 16, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0efec" vertical={false} />
                <XAxis
                  dataKey="status"
                  tick={{ fontSize: 12, fill: '#52514e' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e5e2' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#52514e' }}
                  tickLine={false}
                  axisLine={false}
                  width={56}
                  tickFormatter={(v) => numberFmt.format(v)}
                />
                <Tooltip
                  cursor={{ fill: '#f7f7f5' }}
                  formatter={(v: number) => [numberFmt.format(v), 'Listings']}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={56}>
                  <LabelList
                    dataKey="count"
                    position="top"
                    fontSize={11}
                    fill="#52514e"
                    formatter={(v: number) => numberFmt.format(v)}
                  />
                  {statusData.map((d) => (
                    <Cell key={d.status} fill={d.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Top Categories"
            note="By total listings — always across all categories"
            isLoading={overallLoading}
            isEmpty={categoryChartData.length === 0}
          >
            <RankedBars data={categoryChartData} />
          </ChartCard>
        </div>

        {/* Breakdowns: not scoped by the category filter */}
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ChartCard
            title="Listings by Vertical"
            note="Top-level category, all categories"
            isLoading={isLoading}
            isEmpty={!stats?.by_vertical?.length}
          >
            <RankedBars data={bucketRows(stats?.by_vertical, 'Uncategorised')} />
          </ChartCard>
          <ChartCard
            title="Listings by Intent"
            note="For sale, for rent, for business and so on"
            isLoading={isLoading}
            isEmpty={!stats?.by_intent?.length}
          >
            <RankedBars data={bucketRows(stats?.by_intent, 'No intent')} />
          </ChartCard>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <RankedTable
            title="Top Locations"
            note="Where listings are, top 15"
            column="Location"
            rows={bucketRows(stats?.top_locations, 'No location')}
            total={stats?.total ?? 0}
            isLoading={isLoading}
          />
          <RankedTable
            title="Top Sellers"
            note="Organisations by listing count, top 15"
            column="Organisation"
            rows={bucketRows(stats?.top_sellers, 'Individuals (no organisation)')}
            total={stats?.total ?? 0}
            isLoading={isLoading}
          />
        </div>

        <div className="mb-6 overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-6 py-4">
            <h3 className="text-base font-semibold text-gray-900">Most Viewed Listings</h3>
            <p className="mt-0.5 text-xs text-gray-400">All-time page views, top 10</p>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Listing', 'Status', 'Views'].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan={3} className="px-6 py-3">
                    <Skeleton className="h-4 w-full" />
                  </td>
                </tr>
              ) : !stats?.most_viewed?.length ? (
                <tr>
                  <td colSpan={3} className="px-6 py-8 text-center text-sm text-gray-400">
                    No views recorded for this scope
                  </td>
                </tr>
              ) : (
                stats.most_viewed.map((l) => (
                  <tr key={l.eid} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm">
                      <Link to={`/admin/listings/${l.eid}`} className="text-blue-600 hover:underline">
                        {l.name || `Listing ${l.eid}`}
                      </Link>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600">
                      {l.status ? l.status.charAt(0) + l.status.slice(1).toLowerCase() : '-'}
                    </td>
                    <td className="px-6 py-3 text-sm tabular-nums text-gray-900">{numberFmt.format(l.views)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Detailed table */}
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-6 py-4">
            <h3 className="text-base font-semibold text-gray-900">Detailed Statistics</h3>
            <p className="mt-0.5 text-xs text-gray-400">
              Average price and time-to-sell have no backend source and read 0
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Category', 'Total Listings', 'Active Listings', 'Avg. Price', 'Avg. Time to Sell'].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {overallLoading &&
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 5 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-20" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {!overallLoading && topCategories.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-sm text-gray-400">
                      No categories returned
                    </td>
                  </tr>
                )}

                {!overallLoading &&
                  topCategories.map((c) => (
                    <tr key={c.etid} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {c.name || `Category ${c.etid}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm tabular-nums text-gray-600">
                        {numberFmt.format(c.count)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm tabular-nums text-gray-600">
                        {perCategoryLoading && activeByEtid[c.etid] === undefined ? (
                          <Skeleton className="h-4 w-12" />
                        ) : (
                          numberFmt.format(activeByEtid[c.etid] ?? 0)
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm tabular-nums text-gray-300">
                        0
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm tabular-nums text-gray-300">
                        0
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingAnalytics;
